/**
 * useResumeProtocol — two-tier resume + visibility-revalidation policy.
 *
 * Mount: if a fresh draft exists for any known survey config and there is
 * no live server session (or it has expired), bind a fresh session and
 * replay the draft as a partial submit. The draft survives the re-bind
 * because the survey upserts on `draftId` (24 h client tier).
 *
 * On `visibilitychange → visible`: idempotently revalidate both TTLs.
 * We do NOT call `createSession` from here to avoid double-creating;
 * the mount-time path already covers fresh-draft + missing-token.
 */

import { onBeforeUnmount, onMounted } from "vue";

import { useApiStore } from "@src/stores/useApiStore.ts";
import { useSessionExpiryStore } from "@src/stores/useSessionExpiryStore.ts";
import { typedStorage } from "@src/utils/typedStorage.ts";
import { DEFAULT_SURVEY_CONFIG } from "@src/config/survey.ts";

function readRestoredDraft(): {
    configId: string;
    draftId: string;
    formData: Record<string, unknown>;
    flowType: string | null;
} | null {
    if (typeof localStorage === "undefined") return null;
    // Today the app ships one survey config; iterate the registry-of-one
    // by reading DEFAULT_SURVEY_CONFIG. If more configs come online they
    // appear here — keep the loop shape so the addition is mechanical.
    const config = DEFAULT_SURVEY_CONFIG;
    const blobKey = `survey-${config.id}`;
    const draftIdKey = `survey-${config.id}-draftId`;
    const blob = typedStorage.get<{
        flowType?: string | null;
        formData?: Record<string, unknown>;
    } | null>(blobKey, null);
    const draftId = typedStorage.get<string | null>(draftIdKey, null);
    if (!blob || !draftId) return null;
    return {
        configId: config.id,
        draftId,
        formData: blob.formData ?? {},
        flowType: blob.flowType ?? null,
    };
}

export function useResumeProtocol() {
    const api = useApiStore();
    const sessionExpiry = useSessionExpiryStore();

    async function tryResumeFromDraft(): Promise<void> {
        // Skip if the draft tier itself has already expired — the mount-time
        // logic in useApiStore cleared `survey-*` already, so this is a no-op.
        if (sessionExpiry.isDraftExpired) return;
        const draft = readRestoredDraft();
        if (!draft) return;

        // Only auto-resume when there's no live server session. If the
        // session is fresh, the draft survives without re-binding.
        const hasFreshSession = Boolean(sessionExpiry.sessionToken)
            && !sessionExpiry.isServerSessionExpired;
        if (hasFreshSession) return;

        try {
            await api.createSession();
            await api.replayPartial(draft.draftId, {
                flow: draft.flowType ?? "home",
                ...draft.formData,
            });
        } catch {
            // Best-effort: if the server is unreachable the user still
            // sees their draft on next interaction.
        }
    }

    function onVisibilityChange(): void {
        if (typeof document === "undefined") return;
        if (document.visibilityState !== "visible") return;
        sessionExpiry.clearExpiredOnly();
    }

    onMounted(() => {
        if (typeof document !== "undefined") {
            document.addEventListener("visibilitychange", onVisibilityChange);
        }
        // Fire-and-forget: the resume path is best-effort.
        void tryResumeFromDraft();
    });

    onBeforeUnmount(() => {
        if (typeof document !== "undefined") {
            document.removeEventListener("visibilitychange", onVisibilityChange);
        }
    });
}
